import React from 'react';
import { IAccordionSlice } from '../../preload/slices/AccordionSlice';
import { IContentColumnsSlice } from '../../preload/slices/ContentColumnsSlice';
import { IContentSlice } from '../../preload/slices/ContentSlice';
import { IFormSlice } from '../../preload/slices/FormSlice';
import { IGallerySlice } from '../../preload/slices/GallerySlice';
import { IHeroSlice } from '../../preload/slices/HeroSlice';
import { IIframeSlice } from '../../preload/slices/IframeSlice';
import { IImageContentSlice } from '../../preload/slices/ImageContentSlice';
import { IJobListingSlice } from '../../preload/slices/JobListingSlice';
import { ILinkBlockSlice } from '../../preload/slices/LinkBlockSlice';
import { IMediaHeroSlice } from '../../preload/slices/MediaHeroSlice';
import { IProductCategoryLinksSlice } from '../../preload/slices/ProductCategoryLinksSlice';
import { IPromotionListSlice } from '../../preload/slices/PromotionListSlice';
import { IQuickLinksSlice } from '../../preload/slices/QuickLinksSlice';
import { IRelatedContentSlice } from '../../preload/slices/RelatedContentSlice';
import { IVideoSlice } from '../../preload/slices/VideoSlice';
import { IImageSlice } from '../../preload/slices/ImageSlice';
import Skeleton from '../../styled/Skeleton';
import SliceLoader from './SliceLoader';
import ProductSlice, { IProductSlice } from '../../preload/slices/ProductSlice';
import NavigationSlice, { INavigationSlice } from '../../preload/slices/NavigationSlice';
import FlowboxSlice, { IFlowboxSlice } from '../../preload/slices/FlowboxSlice';
import { IHotspotsSlice } from '../../preload/slices/HotspotsSlice';
import ProductListingSlice, {
  IProductListingSlice,
} from '../../preload/slices/ProductListingSlice';

import MultiImageHeroSlice, {
  IMultiImageHeroSlice,
} from '../../preload/slices/MultiImageHeroSlice';
import CountdownSlice, { ICountdownSlice } from '../../preload/slices/CountdownSlice';

const AccordionSlice = React.lazy(() => import('../../preload/slices/AccordionSlice'));
const ContentColumnsSlice = React.lazy(() => import('../../preload/slices/ContentColumnsSlice'));
const ContentSlice = React.lazy(() => import('../../preload/slices/ContentSlice'));
const FormSlice = React.lazy(() => import('../../preload/slices/FormSlice'));
const GallerySlice = React.lazy(() => import('../../preload/slices/GallerySlice'));
const HeroSlice = React.lazy(() => import('../../preload/slices/HeroSlice'));
const HotspotSlice = React.lazy(() => import('../../preload/slices/HotspotsSlice'));
const IframeSlice = React.lazy(() => import('../../preload/slices/IframeSlice'));
const ImageContentSlice = React.lazy(() => import('../../preload/slices/ImageContentSlice'));
const JobListingSlice = React.lazy(() => import('../../preload/slices/JobListingSlice'));
const LinkBlockSlice = React.lazy(() => import('../../preload/slices/LinkBlockSlice'));
const MediaHeroSlice = React.lazy(() => import('../../preload/slices/MediaHeroSlice'));
const PromotionListSlice = React.lazy(() => import('../../preload/slices/PromotionListSlice'));
const QuickLinksSlice = React.lazy(() => import('../../preload/slices/QuickLinksSlice'));
const RelatedContentSlice = React.lazy(() => import('../../preload/slices/RelatedContentSlice'));
const VideoSlice = React.lazy(() => import('../../preload/slices/VideoSlice'));
const ProductCategoryLinksSlice = React.lazy(
  () => import('../../preload/slices/ProductCategoryLinksSlice'),
);
const ImageSlice = React.lazy(() => import('../../preload/slices/ImageSlice'));

interface ISliceProps extends ISlice {
  order: number;
  inPanel?: boolean;
  isHeadingSlice?: boolean;
}

const Slice = ({
  id,
  inPanel,
  isHeadingSlice = false,
  order,
  sliceType,
  ...props
}: ISliceProps) => {
  switch (sliceType) {
    case 'accordions':
      return (
        <SliceLoader
          component="slices/AccordionSlice"
          loader={<Skeleton height="385px" />}
          isAccent
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IAccordionSlice)}>
          <AccordionSlice {...(props as IAccordionSlice)} />
        </SliceLoader>
      );

    case 'content':
      return (
        <SliceLoader
          component="slices/ContentSlice"
          loader={<Skeleton height="500px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IContentSlice)}>
          <ContentSlice
            {...(props as IContentSlice)}
            inPanel={inPanel}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'contentTwoCol':
      return (
        <SliceLoader
          component="slices/ContentColumnsSlice"
          loader={<Skeleton height="500px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IContentColumnsSlice)}>
          <ContentColumnsSlice
            {...(props as IContentColumnsSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'form':
      return (
        <SliceLoader
          component="slices/FormSlice"
          isAccent
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IFormSlice)}>
          <FormSlice
            {...(props as IFormSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'gallery':
      return (
        <SliceLoader
          component="slices/GallerySlice"
          loader={<Skeleton height="500px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IGallerySlice)}>
          <GallerySlice
            {...(props as IGallerySlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'hero':
      return (
        <SliceLoader
          component="slices/HeroSlice"
          loader={<Skeleton height="750px" />}
          spacing={order === 0 ? 'none' : 'low'}
          order={order}
          isHeadingSlice={isHeadingSlice}
          imageLoading={order >= 2 ? 'lazy' : 'eager'}
          {...(props as IHeroSlice)}>
          <HeroSlice
            {...(props as IHeroSlice)}
            inPanel={inPanel}
            imageLoading={order >= 2 ? 'lazy' : 'eager'}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'iframe':
      return (
        <SliceLoader
          component="slices/IframeSlice"
          loader={<Skeleton height="811px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IIframeSlice)}>
          <IframeSlice {...(props as IIframeSlice)} />
        </SliceLoader>
      );

    case 'imageContent':
      return (
        <SliceLoader
          component="slices/ImageContentSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IImageContentSlice)}>
          <ImageContentSlice
            {...(props as IImageContentSlice)}
            inPanel={inPanel}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'jobListing':
      return (
        <SliceLoader
          component="slices/JobListingSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IJobListingSlice)}>
          <JobListingSlice
            {...(props as IJobListingSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'linkBlock':
      return (
        <SliceLoader
          component="slices/LinkBlockSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as ILinkBlockSlice)}>
          <LinkBlockSlice {...(props as ILinkBlockSlice)} />
        </SliceLoader>
      );

    case 'mediaHero':
      return (
        <SliceLoader
          component="slices/MediaHeroSlice"
          loader={
            <Skeleton
              mobileRatio={(props as IMediaHeroSlice).blockRatioMobile}
              desktopRatio={(props as IMediaHeroSlice).blockRatioDesktop}
            />
          }
          spacing={order === 0 ? 'none' : 'default'}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IMediaHeroSlice)}>
          <MediaHeroSlice
            {...(props as IMediaHeroSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'multiimagehero':
      return (
        <SliceLoader
          component="slices/MultiImageHeroSlice"
          loader={
            <Skeleton
              mobileRatio={(props as IMultiImageHeroSlice).mobileRatio}
              desktopRatio={(props as IMultiImageHeroSlice).desktopRatio}
            />
          }
          isHeadingSlice={isHeadingSlice}
          spacing={order === 0 ? 'none' : 'default'}
          order={order}
          {...(props as IMultiImageHeroSlice)}>
          <MultiImageHeroSlice {...(props as IMultiImageHeroSlice)} />
        </SliceLoader>
      );

    case 'productCategoryLinks':
      return (
        <SliceLoader
          component="slices/ProductCategoryLinksSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IProductCategoryLinksSlice)}>
          <ProductCategoryLinksSlice inPanel={inPanel} {...(props as IProductCategoryLinksSlice)} />
        </SliceLoader>
      );

    case 'quickLinks':
      return (
        <SliceLoader
          component="slices/QuickLinksSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IQuickLinksSlice)}>
          <QuickLinksSlice
            {...(props as IQuickLinksSlice)}
            inPanel={inPanel}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'relatedContent':
      return (
        <SliceLoader
          component="slices/RelatedContentSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IRelatedContentSlice)}>
          <RelatedContentSlice
            {...(props as IRelatedContentSlice)}
            inPanel={inPanel}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'video':
      return (
        <SliceLoader
          component="slices/VideoSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IVideoSlice)}>
          <VideoSlice
            {...(props as IVideoSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'productListing':
      return (
        <SliceLoader
          component="slices/ProductListingSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IProductSlice)}>
          <ProductListingSlice
            {...(props as IProductListingSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'product':
      return (
        <SliceLoader
          component="slices/ProductSlice"
          id={id}
          isHeadingSlice={isHeadingSlice}
          loader={<Skeleton height="800px" />}
          order={order}
          spacing="none"
          {...(props as IProductSlice)}>
          <ProductSlice
            {...(props as IProductSlice)}
            id={id}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'promotionList':
      return (
        <SliceLoader
          component="slices/PromotionListSlice"
          spacing={order === 0 ? 'none' : undefined}
          loader={<Skeleton height="500px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IPromotionListSlice)}>
          <PromotionListSlice
            {...(props as IPromotionListSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'navigationSlice':
      return (
        <SliceLoader
          component="slices/NavigationSlice"
          loader={<Skeleton height="180px" />}
          spacing="low"
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as INavigationSlice)}>
          <NavigationSlice {...(props as INavigationSlice)} id={id} />
        </SliceLoader>
      );

    case 'flowbox':
      return (
        <SliceLoader
          component="slices/FlowboxSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IFlowboxSlice)}>
          <FlowboxSlice
            {...(props as IFlowboxSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'hotspots':
      return (
        <SliceLoader
          component="slices/HotspotSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IHotspotsSlice)}>
          <HotspotSlice
            {...(props as IHotspotsSlice)}
            titleHeadingLevel={isHeadingSlice ? 1 : undefined}
          />
        </SliceLoader>
      );

    case 'image':
      return (
        <SliceLoader
          component="slices/ImageSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as IImageSlice)}>
          <ImageSlice {...(props as IImageSlice)} />
        </SliceLoader>
      );

    case 'countdownSlice':
      return (
        <SliceLoader
          component="slices/CountdownSlice"
          loader={<Skeleton height="800px" />}
          order={order}
          isHeadingSlice={isHeadingSlice}
          {...(props as ICountdownSlice)}>
          <CountdownSlice {...(props as ICountdownSlice)} />
        </SliceLoader>
      );

    default:
      return null;
  }
};

export default Slice;
